import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { can, type Me } from '../auth/me';
import { isLocalPath } from './localPath';
import { leaveGuarded } from '../ui/unsavedCore';
import { askOpenInCatalog } from './CatalogOpenAsk';

/**
 * Ctrl+klik on a line (owner 2026-10-23 20:53: „klikniecie w pozycje z wcisnietym klawiszem ctrl niech przeniesie do
 * katalogu do tej pozycji z otwarta edycja”): a line of the quote, the packlist or „Dopożyczenia” opens its catalog
 * entry with the editor open — our catalog (`/katalog?edytuj=<itemId>`) or the supplier's
 * (`/katalog/dostawcy/<supplierId>?edytuj=<priceId>`), with `powrot=` the page it came from.
 *
 * The key: on a Mac Ctrl+click is the secondary click — Safari sends `contextmenu` (and no `click`); ⌘+click stays the
 * selection of lines (#116). On Windows / Linux Ctrl+click is the selection already, so there it is Alt+click.
 */

export type CatalogTarget =
  | { kind: 'item'; itemId: string }
  | { kind: 'supplier'; supplierId: string; priceId: string }
  | { kind: 'supplier'; supplierId: string; itemId: string }
  /** A supplier's position known by its name only (the packlist's „Odbiór”): its catalog, searched. */
  | { kind: 'supplier'; supplierId: string; name: string };

const ID = /^[\w-]{1,64}$/;

/** Where a quote line goes: a supplier's line to its position, our equipment to the item; services, transport, own lines nowhere. */
export function lineTarget(l: { type: string; itemId: string | null; supplierId?: string | null | undefined; supplierPriceId?: string | null | undefined }): CatalogTarget | null {
  if (l.supplierId && l.supplierPriceId) return { kind: 'supplier', supplierId: l.supplierId, priceId: l.supplierPriceId };
  if (l.supplierId && l.itemId) return { kind: 'supplier', supplierId: l.supplierId, itemId: l.itemId };
  if (l.supplierId) return null;
  return l.type === 'item' && l.itemId ? { kind: 'item', itemId: l.itemId } : null;
}

/** Whether the person may look there at all: our catalog, or the suppliers' catalogs. */
export const mayOpen = (me: Me | null | undefined, t: CatalogTarget | null): t is CatalogTarget =>
  !!me && !!t && can(me, t.kind === 'item' ? 'catalog.items' : 'subrentals.prices', 'view');

/** The catalog's address with the entry's editor open; `back`: where „Wróć” goes (a local address only). */
export function catalogEditUrl(t: CatalogTarget, back?: string): string {
  const q = new URLSearchParams();
  if (t.kind === 'item') q.set('edytuj', t.itemId);
  else if ('priceId' in t) q.set('edytuj', t.priceId);
  else if ('itemId' in t) q.set('edytuj', `item:${t.itemId}`);
  else q.set('szukaj', t.name);
  if (back && isLocalPath(back)) q.set('powrot', back);
  const path = t.kind === 'item' ? '/katalog' : `/katalog/dostawcy/${encodeURIComponent(t.supplierId)}`;
  return `${path}?${q.toString()}`;
}

/** `?edytuj=<itemId>&powrot=…` of our catalog, or null. */
export function parseCatalogEdit(params: URLSearchParams): { id: string; back: string | null } | null {
  const id = params.get('edytuj') ?? '';
  if (!ID.test(id)) return null;
  const back = params.get('powrot');
  return { id, back: back && isLocalPath(back) ? back : null };
}

/** Never from these: the drag handles (⋮⋮ of a line, a section, a catalog item) and menus. */
const NEVER = '[data-section-grip], .q-grip, .cat-handle, [role="menu"], [role="menuitem"], [role="listbox"], [role="option"], [aria-haspopup]';
/** A click there is the control's own (Alt+click on Windows); a button marked `data-catalog-link` lets it through. */
const OWN_CLICK = 'button:not([data-catalog-link]), a, select, label, summary';

export const isMac = (): boolean => /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

type Gesture = { type: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean; shiftKey: boolean; target: EventTarget | null };

/** Whether the mouse event asks to open the line in the catalog (`mac`: Ctrl+click as `contextmenu`, or its click). */
export function catalogGesture(e: Gesture, mac: boolean): boolean {
  const el = e.target instanceof Element ? e.target : null;
  if (el?.closest(NEVER)) return false;
  if (mac) return e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && (e.type === 'contextmenu' || e.type === 'click');
  return e.type === 'click' && e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && !el?.closest(OWN_CLICK);
}

/** The tooltip of the line's name. */
export const catalogTitle = (mac: boolean = isMac()): string => (mac ? 'Ctrl+klik: otwórz w katalogu' : 'Alt+klik: otwórz w katalogu');

/**
 * The row's handlers: `onContextMenu` and `onClick` return true when they took the event (the row then does nothing
 * else with it — no selection). One gesture opens once (a Mac browser may send `contextmenu` and `click`).
 */
export function useCatalogGesture(open: (() => void) | undefined) {
  const last = useRef(0);
  const handle = (e: Gesture & { preventDefault: () => void }): boolean => {
    if (!open || !catalogGesture(e, isMac())) return false;
    e.preventDefault();
    const now = Date.now();
    if (now - last.current > 800) {
      last.current = now;
      open();
    }
    return true;
  };
  return { onContextMenu: handle, onClick: handle };
}

/**
 * Opens a target in the catalog, with the current page as the way back (the step is a new history entry: „Wstecz”
 * returns). Owner 2026-10-24 10:53: a changed form asks first („Masz niezapisane zmiany”, #128), then „Przejść do
 * edycji „<name>” w katalogu?” — only „Przejdź” goes.
 */
export function useCatalogOpener(): (t: CatalogTarget, name: string) => void {
  const navigate = useNavigate();
  const location = useLocation();
  return (t, name) => {
    const url = catalogEditUrl(t, `${location.pathname}${location.search}`);
    leaveGuarded(() => askOpenInCatalog(name, () => navigate(url)));
  };
}
